import { differenceInSeconds } from 'date-fns'
import { getBaseApiUrl } from 'global/helpers/environment'
import { createServerError } from 'global/helpers/requestError'
import { IDENTITY_PREFIX, postRequest } from 'global/helpers/requests'
import type { Auth, IdToken, Jwt, User } from './Auth.types'

export class AuthImpl implements Auth {
  async sendPasswordResetEmail(email: string): Promise<void> {
    return postRequest<void>('users/resetpassword', {
      body: { email },
    }, IDENTITY_PREFIX)
  }

  async verifyPasswordResetCode(oobCode: string): Promise<void> {
    return postRequest<void>('users/resetpassword/verify', {
      body: { oobCode },
    }, IDENTITY_PREFIX)
  }

  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return postRequest<void>('users/resetpassword/confirm', {
      body: {
        oobCode,
        newPassword,
      },
    }, IDENTITY_PREFIX)
  }

  async signInWithEmailAndPassword(email: string, password: string): Promise<IdToken> {
    clearTokens()
    const btoaString = `${email}:${password}`
    const jwt = await fetchToken('idToken', `Basic ${btoa(btoaString)}`)
    return decodeJwt(jwt)
  }

  signOut(): void {
    clearTokens()
  }

  async getCurrentUser(): Promise<User | null> {
    const token = await retrieveToken('idToken')
    const idToken: IdToken = decodeJwt(token)
    if (token && idToken) {
      return {
        idToken: token,
        uid: idToken.sub,
        email: idToken.email,
        displayName: idToken.displayName,
        language: idToken.language?.toLowerCase().replace('_', '-'),
        timezone: idToken.timezone,
      }
    } else {
      return null
    }
  }
}

export const getToken = async (groupId?: string): Promise<string> => {
  if (!groupId) {
    return ''
  }
  return await retrieveToken(`group-${groupId}`) ?? ''
}

const fetchToken = async (key: string, authorization: string): Promise<string> => {
  console.log(`Fetching new token for '${key}'`)
  const baseApiUrl = getBaseApiUrl()
  const path = key === 'idToken'
    ? `/api/${IDENTITY_PREFIX}/${import.meta.env.VITE_API_VERSION}/token`
    : `/api/${IDENTITY_PREFIX}/${import.meta.env.VITE_API_VERSION}/groups/${key.replace('group-', '')}/token`
  const response: Response = await fetch(`${baseApiUrl}${path}`, {
    method: 'POST',
    headers: {
      Authorization: authorization,
    },
  })
  if (!response.ok) {
    if (response.status == 404) {
      window.location.replace('/not-found')
    }
    throw await createServerError(response)
  }
  const jwt = await response.text()
  storeToken(key, jwt)
  return jwt
}

const retrieveToken = async (key: string): Promise<string | null> => {
  const jwt = getTokenIfValid(key)
  if (jwt) {
    const totalSeconds = tokenTotalValidityInSeconds(jwt)
    const remainingSeconds = tokenRemainingValidityInSeconds(jwt)
    if (remainingSeconds < totalSeconds / 2) {
      // refresh the token if it expires within half of its total validity duration
      return fetchToken(key, `Bearer ${jwt}`)
    } else {
      return jwt
    }
  }
  if (key !== 'idToken') {
    const idToken = await retrieveToken('idToken')
    if (idToken) {
      // attempt to fetch a token for the requested key using the `idToken`
      return fetchToken(key, `Bearer ${idToken}`)
    }
  }
  // all attempts above failed
  return null
}

const getTokenIfValid = (key: string): string | null => {
  const jwt = localStorage.getItem(key)
  if (!jwt) {
    return null
  }
  const remainingSeconds = tokenRemainingValidityInSeconds(jwt)
  if (remainingSeconds > 0) {
    return jwt
  } else {
    console.log(`Token '${key}' expired ${Math.abs(remainingSeconds)} seconds ago`)
    clearToken(key)
    return null
  }
}

const storeToken = (key: string, jwt: string): void => {
  console.log(`Storing token '${key}'`)
  localStorage.setItem(key, jwt)
}

const clearTokens = (): void => {
  console.log('Clearing all tokens')
  clearToken('idToken')
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith('group-')) {
      clearToken(key)
    }
  }
}

const clearToken = (key: string): void => {
  console.log(`Clearing token '${key}'`)
  localStorage.removeItem(key)
}

export const decodeJwt = (jwt: string | null): any => {
  if (!jwt || jwt.length === 0) {
    return null
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_header, body, _signature] = jwt.split('.', 3)
  // body is encoded using Base64 URL safe encoding
  const base64Body = body!.replace(/-/g, '+').replace(/_/g, '/')
  const json = atob(base64Body)
  return JSON.parse(json)
}

const tokenTotalValidityInSeconds = (jwt: string): number => {
  const parsed: Jwt = decodeJwt(jwt)
  return differenceInSeconds((parsed.iat || 0) * 1000, (parsed.exp || 0) * 1000)
}

const tokenRemainingValidityInSeconds = (jwt: string): number => {
  const parsed: Jwt = decodeJwt(jwt)
  return differenceInSeconds((parsed.exp || 0) * 1000, new Date())
}
